import { Capacitor } from '@capacitor/core';
/** Reports this account may still start: `left` is null when there is no limit. */
export type Allowance = { limit: number | null; used: number; left: number | null };
export type User = { id: string; email: string; name: string; agreementSigned?: boolean; allowance?: Allowance };
export type AgreementState = { version: string; signed: { fullName: string; signedAt: string; version: string } | null };

export type Suggestion = {
  gnafId: string;
  displayName: string;
  line1: string;
  line2: string;
  propertyType: string | null;
};

export type ToneKey = 'ok' | 'strong' | 'warn' | 'high' | 'neutral' | 'cyan' | 'pink';

export type SignalRow = { k: string; s: string; v: string; t: string; tone: ToneKey | '' };
export type Stat = { k: string; v: string; s: string };

export type Module = {
  key: string;
  num: string;
  title: string;
  sub: string;
  tag: string;
  tone: ToneKey;
  intro: string;
  hero: { label: string; value: string; unit: string; caption: string; tag: string; tone: ToneKey };
  stats: Stat[];
  rows: SignalRow[];
  note: string;
};

export type Dossier = {
  reportId: string;
  generatedAt: string;
  gnafId: string;
  address: {
    line1: string;
    locality: string;
    state: string;
    postcode: string;
    full: string;
    suburbLabel: string;
  };
  attributes: string;
  landSqm: number | null;
  hero: {
    midpoint: number | null;
    listed: number | null;
    ceiling: number | null;
    rangeLow: number | null;
    confidence: number | null;
    confidenceBand: 'HIGH CONFIDENCE' | 'MODERATE CONFIDENCE' | 'MEDIUM CONFIDENCE' | 'LIMITED DATA' | 'ESTIMATE' | null;
    secondOpinion: string | null;
    pill: string;
    caption: string;
    verdict: string;
    verdictReason: string;
    /** Price rating, 0–100: 70 means the asking price matches the evidence. */
    rating?: number | null;
  };
  /** Absent on reports built before the offer window. */
  offer?: OfferWindow | null;
  history: { pct: number | null; lead: string; body: string; note: string };
  risks: { safe: number; tracked: number; flagged: number; body: string; note: string };
  modules: Module[];
  reliability: { confirmed: number; attempted: number; score: number };
  sources: { endpoint: string; ok: boolean; costCents: number; reason?: string }[];
  costCents: number;
  /** Distinct datasets the report drew on — what "sources" honestly means. */
  sourceGroups: number;
  photoUrl: string | null;
  streetView: {
    lat: number;
    lng: number;
    panoLat: number | null;
    panoLng: number | null;
    captured: string | null;
    panoId: string | null;
  } | null;
  targetPrice: number | null;
};

export type ReportState = {
  id: string;
  address: string;
  line1: string;
  locality: string;
  status: 'assembling' | 'ready' | 'failed';
  step: number;
  steps: string[];
  percent: number;
  sectionsReady: number;
  sectionsTotal: number;
  providersConfirmed: number;
  providersTotal: number;
  currentStage: string | null;
  error: string | null;
  costCents: number;
  createdAt: string;
  completedAt: string | null;
  dossier: Dossier | null;
};

export type ReportSummary = {
  id: string;
  /** The property, not the report — a refresh assembles this address again. */
  gnafId: string;
  address: string;
  /** Split for the portfolio card: "135 HEAL ST" over "NEW FARM QLD". */
  line1: string;
  locality: string;
  archived: boolean;
  status: 'assembling' | 'ready' | 'failed';
  step: number;
  costCents: number;
  createdAt: string;
  completedAt: string | null;
  verdict: string | null;
  targetPrice: number | null;
};

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/**
 * Review mode: the team's review key, kept in this browser, lets the report
 * screens open any tester's report. Set by opening the app with ?review=<key>
 * (the stats dashboard's OPEN links do this).
 */
const REVIEW_KEY = 'rb_review_key';
export function reviewKey(): string | null {
  try {
    return localStorage.getItem(REVIEW_KEY);
  } catch {
    return null;
  }
}
export function setReviewKey(key: string | null): void {
  try {
    if (key) localStorage.setItem(REVIEW_KEY, key);
    else localStorage.removeItem(REVIEW_KEY);
  } catch {
    /* storage blocked: review mode lasts this page only */
  }
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const key = reviewKey();
  const headers: Record<string, string> = {};
  if (init?.body) headers['Content-Type'] = 'application/json';
  if (key) headers['x-review-token'] = key;
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: Object.keys(headers).length ? headers : undefined,
    ...init,
  });
  const body = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(res.status, body.error ?? 'Something went wrong.');
  return body;
}

export type Meta = { sources: number; modules: number; estimatedMinutes: number; moduleTitles?: string[] };

export const api = {
  meta: () => call<Meta>('/api/meta'),
  me: () => call<{ user: User | null }>('/api/auth/me'),
  acceptTerms: (body: { version: string; over18: boolean }) => call<{ accepted: true }>('/api/terms/accept', { method: 'POST', body: JSON.stringify(body) }),
  agreement: () => call<AgreementState>('/api/agreement'),
  signAgreement: (body: { version: string; fullName: string; over18: boolean; agree: boolean }) =>
    call<{ signed: NonNullable<AgreementState['signed']> }>('/api/agreement/sign', { method: 'POST', body: JSON.stringify(body) }),
  signIn: (email: string, password: string) =>
    call<{ user: User }>('/api/auth/signin', { method: 'POST', body: JSON.stringify({ email, password }) }),
  signUp: (email: string, password: string, name: string | undefined, acceptTerms: boolean) =>
    call<{ user: User }>('/api/auth/signup', { method: 'POST', body: JSON.stringify({ email, password, name, acceptTerms }) }),
  signOut: () => call<{ ok: true }>('/api/auth/signout', { method: 'POST' }),
  deleteAccount: () =>
    call<{ ok: true }>('/api/account', { method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ confirm: 'DELETE' }) }),

  search: (q: string, signal?: AbortSignal) =>
    call<{ results: Suggestion[] }>(`/api/search?q=${encodeURIComponent(q)}`, { signal }),

  createReport: (gnafId: string, displayName: string, buyerFit?: BuyerFit | null) =>
    call<{ id: string; steps: string[] }>('/api/reports', {
      method: 'POST',
      body: JSON.stringify({ gnafId, displayName, buyerFit }),
    }),
  report: (id: string) => call<ReportState>(`/api/reports/${id}`),
  resumeReport: (id: string) => call<{ ok: true }>(`/api/reports/${id}/resume`, { method: 'POST' }),
  reports: () => call<{ reports: ReportSummary[] }>('/api/reports'),
  /** A report bought in the iOS app: the server checks Apple's signature, then credits it. */
  /** A store purchase to credit: Apple's signed transaction on iOS, Google's purchase token on Android. */
  storePurchase: (receipt: string) =>
    Capacitor.getPlatform() === 'android'
      ? call<{ ok: true; transactionId: string; allowance: Allowance }>('/api/purchases/google', { method: 'POST', body: JSON.stringify({ token: receipt }) })
      : call<{ ok: true; transactionId: string; allowance: Allowance }>('/api/purchases/apple', { method: 'POST', body: JSON.stringify({ jws: receipt }) }),
  deleteReport: (id: string) => call<{ ok: true }>(`/api/reports/${id}`, { method: 'DELETE' }),
  setArchived: (id: string, archived: boolean) =>
    call<{ ok: true; archived: boolean }>(`/api/reports/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ archived }),
    }),

  buyerFit: (id: string) => call<{ buyerFit: BuyerFit | null }>(`/api/reports/${id}/buyer-fit`),
  saveBuyerFit: (id: string, fit: BuyerFit) =>
    call<{ ok: true }>(`/api/reports/${id}/buyer-fit`, { method: 'PUT', body: JSON.stringify(fit) }),
};

/**
 * What the buyer is looking for, asked fresh per report. Four groups of chips,
 * each capped; never anything financial — the screen says so and has no field
 * for it.
 */
export type OfferMethod = {
  key: 'M1' | 'M2' | 'M3' | 'M4';
  setAside?: true;
  name: string;
  mid: number | null;
  confidence: 'High' | 'Medium' | 'Low';
  why: string;
  weight: number;
};

export type OfferWindow = {
  mid: number;
  low: number;
  high: number;
  halfWidth: number;
  confidence: 'High' | 'Medium' | 'Low';
  methods: OfferMethod[];
  evidence: number | null;
  indicative: { price: number; source: 'agent' | 'buyer'; used: number; weight: number; note: string | null } | null;
  stale: { days: number; suburbDays: number } | null;
  capped: boolean;
  rating: { score: number; label: string; reason: string } | null;
  /** Absent on reports built before offers could be rated. */
  rate?: { evidence: number; thin: boolean } | null;
  offerBands?: { label: string; from: number | null; to: number | null }[] | null;
  why: string[];
  comparables?: { address: string; price: number; adjusted: number; date: string | null; distKm: number | null; beds: number | null; baths: number | null; landSqm: number | null }[];
  lastSale?: { price: number; date: string } | null;
};

export type BuyerFit = {
  buying: string[];
  matters: string[];
  mustHaves: string[];
  stage: string[];
  /** Optional: the price the buyer is working from, and whether it is the agent's guide or their own. */
  indicativePrice?: number | null;
  indicativeSource?: 'agent' | 'buyer' | null;
  /** What the buyer told us about the house. Enums, never labels. */
  house?: HouseAnswers | null;
};

export type HouseAnswers = {
  finish?: 'needs_work' | 'original' | 'good' | 'updated' | 'high_spec' | 'new_build' | null;
  changed_since_sale?: 'yes' | 'no' | 'unknown' | null;
  extra?: 'none' | 'extension' | 'granny' | 'pool' | null;
  aspect?: 'ordinary' | 'main_road' | 'outlook' | 'water' | null;
  land_form?: 'flat' | 'sloping' | 'steep' | 'battleaxe' | null;
  site?: 'none' | 'possible' | 'approved' | null;
};
